import React from "react";
import { motion } from "framer-motion";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiTrash2
} from "react-icons/fi";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import DelReferee from "../../buttons/DelReferee";
import moment from "../../helpers/moment";

/**
 * Modern card-based display for guardian/referee information
 *
 * Features:
 * - Responsive grid layout (1 col mobile, 2 cols md and up)
 * - Staggered entrance animations
 * - Hover effects and transitions
 * - Icon-based information display
 * - Modern delete button with confirmation
 * - Accessibility-focused design
 *
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of referee/guardian objects
 */
const TblReferee = ({ data }) => {
  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  // Card animation variants
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
  };

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <FiUser className="mx-auto h-12 w-12 text-neutral-400 mb-3" />
          <p className="text-neutral-500 text-lg">No guardian/referee information available</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      role="list"
      aria-label="Guardian and referee information"
    >
      {data.map((referee, index) => (
        <motion.div
          key={referee._id || index}
          variants={cardVariants}
          whileHover={{
            scale: 1.02,
            transition: { duration: 0.2 }
          }}
          role="listitem"
        >
          <Card
            variant="elevated"
            padding="none"
            className="h-full flex flex-col hover:shadow-xl transition-shadow duration-300"
          >
            {/* Header Section */}
            <CardHeader className="bg-gradient-to-br from-primary-50 to-primary-100 border-b border-primary-200 p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-primary-600 text-white rounded-lg">
                      <FiUser className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <CardTitle className="text-neutral-800 uppercase tracking-wide text-base md:text-lg">
                      {referee.firstname} {referee.middlename} {referee.lastname}
                    </CardTitle>
                  </div>
                </div>
              </div>
            </CardHeader>

            {/* Content Section */}
            <CardContent className="flex-1 p-6 space-y-4">
              {/* Phone Number */}
              <div className="flex items-start gap-3 group">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg group-hover:bg-blue-100 transition-colors">
                  <FiPhone className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    Phone Number
                  </p>
                  <p className="text-sm md:text-base font-semibold text-neutral-800 break-words">
                    {referee.phonenumber || "Not provided"}
                  </p>
                </div>
              </div>

              {/* Email Address */}
              <div className="flex items-start gap-3 group">
                <div className="p-2 bg-green-50 text-green-600 rounded-lg group-hover:bg-green-100 transition-colors">
                  <FiMail className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    Email Address
                  </p>
                  <p className="text-sm md:text-base font-medium text-neutral-800 lowercase break-words">
                    {referee.emailaddress || "Not provided"}
                  </p>
                </div>
              </div>

              {/* Home Address */}
              <div className="flex items-start gap-3 group">
                <div className="p-2 bg-purple-50 text-purple-600 rounded-lg group-hover:bg-purple-100 transition-colors">
                  <FiMapPin className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    Home Address
                  </p>
                  <p
                    className="text-sm md:text-base font-medium text-neutral-800 break-words line-clamp-2"
                    title={referee.homeaddress}
                  >
                    {referee.homeaddress || "Not provided"}
                  </p>
                </div>
              </div>

              {/* Created Date */}
              <div className="flex items-start gap-3 group pt-2 border-t border-neutral-100">
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg group-hover:bg-amber-100 transition-colors">
                  <FiCalendar className="h-4 w-4" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-1">
                    Date Added
                  </p>
                  <p className="text-sm md:text-base font-medium text-neutral-800">
                    {moment(referee.createdAt)}
                  </p>
                </div>
              </div>
            </CardContent>

            {/* Footer Section - Delete Action */}
            <div className="p-6 pt-0">
              <div className="border-t border-neutral-200 pt-4">
                {referee.disabled ? (
                  <DelReferee modules={referee} />
                ) : (
                  <Button
                    variant="danger"
                    size="md"
                    fullWidth
                    disabled={true}
                    leftIcon={<FiTrash2 className="h-4 w-4" />}
                    aria-label="Delete referee (disabled)"
                    className="opacity-50 cursor-not-allowed"
                  >
                    Cannot Delete
                  </Button>
                )}
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default TblReferee;
